import { FC, ReactNode, useEffect, useState } from "react";
import { Toast } from "@/components/Common/Toast/Toast";

export type LoadingState = "idle" | "loading" | "success" | "error" | "info";

interface LoadingButtonProps {
  text: string;
  onClick: () => Promise<void> | void;
  id?: string;
  title?: string;
  className?: string;
  disabled?: boolean;
  state?: LoadingState;
  showToast?: boolean;
  toastMessages?: {
    success?: string;
    error?: string;
    loading?: string;
  };
  icon?: ReactNode;
  loadingIcon?: ReactNode;
  successIcon?: ReactNode;
  errorIcon?: ReactNode;
  minLoadingDuration?: number;
  spinnerClassName?: string;
}

/**
 * LoadingButton Component
 * A button with built-in loading, success, error, and info states
 * Based on the screenshot reference showing progress indicators
 *
 * @example
 * <LoadingButton
 *   text="Submit"
 *   onClick={async () => { await saveData(); }}
 *   showToast={true}
 *   toastMessages={{ success: "Saved!", error: "Failed to save" }}
 * />
 */
const LoadingButton: FC<LoadingButtonProps> = ({
  text,
  onClick,
  id,
  title,
  className = "",
  disabled = false,
  state: controlledState,
  showToast = false,
  toastMessages = {},
  icon,
  loadingIcon,
  successIcon,
  errorIcon,
  minLoadingDuration = 0,
  spinnerClassName = "text-gray-200",
}) => {
  const [internalState, setInternalState] = useState<LoadingState>("idle");
  const [toastId, setToastId] = useState<string | null>(null);

  const currentState = controlledState ?? internalState;
  const isDisabled = disabled || currentState === "loading";

  useEffect(() => {
    return () => {
      if (toastId) {
        Toast.dismiss(toastId);
      }
    };
  }, [toastId]);

  const handleClick = async () => {
    if (isDisabled) return;

    const startTime = Date.now();
    setInternalState("loading");

    if (showToast && toastMessages.loading) {
      const id = Toast.loading(toastMessages.loading);
      setToastId(id);
    }

    try {
      await Promise.resolve(onClick());

      // Ensure minimum loading duration for better UX
      const elapsedTime = Date.now() - startTime;
      if (minLoadingDuration > 0 && elapsedTime < minLoadingDuration) {
        await new Promise((resolve) =>
          setTimeout(resolve, minLoadingDuration - elapsedTime),
        );
      }

      setInternalState("success");

      if (showToast) {
        if (toastId) {
          Toast.dismiss(toastId);
        }
        if (toastMessages.success) {
          Toast.success(toastMessages.success);
        }
      }

      // Reset to idle after success animation
      setTimeout(() => {
        setInternalState("idle");
      }, 2000);
    } catch (error) {
      setInternalState("error");

      if (showToast) {
        if (toastId) {
          Toast.dismiss(toastId);
        }
        const errorMessage =
          toastMessages.error ||
          (error instanceof Error ? error.message : "An error occurred");
        Toast.error(errorMessage);
      }

      // Reset to idle after error animation
      setTimeout(() => {
        setInternalState("idle");
      }, 2000);
    }
  };

  const getStateStyles = (): string => {
    switch (currentState) {
      case "loading":
        // darker gray during loading for better contrast and less bluish tone
        return "bg-gray-700 cursor-wait";
      case "success":
        return "bg-green-600 hover:bg-green-700";
      case "error":
        return "bg-red-600 hover:bg-red-700";
      case "info":
        return "bg-blue-600 hover:bg-blue-600";
      default:
        return "bg-blue-600 hover:bg-blue-600";
    }
  };

  const getStateIcon = (): ReactNode => {
    switch (currentState) {
      case "loading":
        return (
          loadingIcon ?? (
            <span
              className={`icon-sync animate-spin text-xl mr-2 ${spinnerClassName}`}
            />
          )
        );
      case "success":
        return (
          successIcon ?? <span className="icon-check_circle text-xl mr-2" />
        );
      case "error":
        return errorIcon ?? <span className="icon-error text-xl mr-2" />;
      default:
        return icon;
    }
  };

  const getButtonText = (): string => {
    if (currentState === "loading") return ""; //Empty state
    if (currentState === "success") return "Success!";
    if (currentState === "error") return "Failed";
    return text;
  };

  const baseStyles =
    "text-center h-[48px] border-solid border-1 rounded-md text-white transition-all duration-200";
  const disabledStyles = isDisabled ? "opacity-50 cursor-not-allowed" : "";
  const stateStyles = getStateStyles();
  const combinedClassName = `${baseStyles} ${stateStyles} ${disabledStyles} ${className}`;

  return (
    <div className="mx-2 block">
      <button
        type="button"
        id={id}
        data-testid={`loading-button-${id}`}
        disabled={isDisabled}
        className={`${combinedClassName} flex flex-row items-center justify-center`}
        onClick={() => void handleClick()}
        title={title}
      >
        {getStateIcon()}
        <p>{getButtonText()}</p>
      </button>
    </div>
  );
};

export default LoadingButton;
